import { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api, type Gallery } from '@/lib/api';

// Loading skeleton component
function Skeleton({ className = '' }: { className?: string }) {
  return (
    <div
      className={`animate-pulse bg-gallery-700 rounded ${className}`}
      aria-hidden="true"
    />
  );
}

// Format currency
function formatCurrency(cents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(cents / 100);
}

// Format date
function formatDate(date: string): string {
  return new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// Debounce hook
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debouncedValue;
}

// Gallery card component
function GalleryCard({ gallery }: { gallery: Gallery }) {
  return (
    <Link
      to={`/galleries/${gallery.id}`}
      className="group bg-gallery-900 rounded-xl overflow-hidden hover:ring-2 hover:ring-accent-500 transition-all"
    >
      {/* Cover Image */}
      <div className="aspect-[4/3] bg-gallery-800 relative overflow-hidden">
        {gallery.coverImageUrl ? (
          <img
            src={gallery.coverImageUrl}
            alt={gallery.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <span className="text-gallery-600 text-6xl">🖼️</span>
          </div>
        )}
        {/* Price Badge */}
        <div className="absolute top-3 right-3">
          <span
            className={`px-2 py-1 text-xs font-medium rounded ${
              gallery.priceType === 'free'
                ? 'bg-green-600/90 text-white'
                : 'bg-gallery-900/90 text-white'
            }`}
          >
            {gallery.entryPriceCents === 0 ? 'Free' : formatCurrency(gallery.entryPriceCents)}
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        <h3 className="font-semibold text-lg mb-1 group-hover:text-accent-400 transition-colors truncate">
          {gallery.title}
        </h3>
        {gallery.description && (
          <p className="text-gallery-400 text-sm mb-3 line-clamp-2">
            {gallery.description}
          </p>
        )}
        <div className="flex items-center justify-between text-sm text-gallery-500">
          <span>{gallery.viewCount.toLocaleString()} views</span>
          <span>{formatDate(gallery.createdAt)}</span>
        </div>
        {/* Tags */}
        {gallery.tags && gallery.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-3">
            {gallery.tags.slice(0, 3).map((tag) => (
              <span
                key={tag}
                className="px-2 py-0.5 text-xs bg-gallery-800 text-gallery-400 rounded"
              >
                {tag}
              </span>
            ))}
            {gallery.tags.length > 3 && (
              <span className="px-2 py-0.5 text-xs text-gallery-500">
                +{gallery.tags.length - 3}
              </span>
            )}
          </div>
        )}
      </div>
    </Link>
  );
}

// Pagination component
function Pagination({
  currentPage,
  totalPages,
  onPageChange,
}: {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}) {
  if (totalPages <= 1) return null;

  const pages: (number | 'ellipsis')[] = [];

  // Always show first page
  pages.push(1);

  // Show ellipsis if current page is far from start
  if (currentPage > 3) {
    pages.push('ellipsis');
  }

  // Show pages around current page
  for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
    if (!pages.includes(i)) {
      pages.push(i);
    }
  }

  // Show ellipsis if current page is far from end
  if (currentPage < totalPages - 2) {
    pages.push('ellipsis');
  }

  // Always show last page
  if (totalPages > 1 && !pages.includes(totalPages)) {
    pages.push(totalPages);
  }

  return (
    <nav className="flex items-center justify-center gap-1" aria-label="Pagination">
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className="px-3 py-2 rounded-lg bg-gallery-800 text-gallery-400 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        aria-label="Previous page"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
      </button>

      {pages.map((page, idx) =>
        page === 'ellipsis' ? (
          <span key={`ellipsis-${idx}`} className="px-3 py-2 text-gallery-500">
            ...
          </span>
        ) : (
          <button
            key={page}
            onClick={() => onPageChange(page)}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              page === currentPage
                ? 'bg-accent-600 text-white'
                : 'bg-gallery-800 text-gallery-400 hover:text-white'
            }`}
            aria-current={page === currentPage ? 'page' : undefined}
          >
            {page}
          </button>
        )
      )}

      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className="px-3 py-2 rounded-lg bg-gallery-800 text-gallery-400 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        aria-label="Next page"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </button>
    </nav>
  );
}

// Common gallery categories/tags
const CATEGORIES = [
  'Digital Art',
  'Photography',
  'Video',
  'Generative',
  'Mixed Media',
  'Abstract',
  'Portrait',
  'Landscape',
];

const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest' },
  { value: 'oldest', label: 'Oldest' },
  { value: 'popular', label: 'Most Popular' },
  { value: 'price_low', label: 'Price: Low to High' },
  { value: 'price_high', label: 'Price: High to Low' },
];

export default function GalleryBrowse() {
  const [searchParams, setSearchParams] = useSearchParams();

  // Parse URL params
  const initialSearch = searchParams.get('q') || '';
  const initialSort = searchParams.get('sort') || 'newest';
  const initialCategory = searchParams.get('category') || '';
  const initialPage = parseInt(searchParams.get('page') || '1', 10);

  const [galleries, setGalleries] = useState<Gallery[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filter state
  const [search, setSearch] = useState(initialSearch);
  const [sort, setSort] = useState(initialSort);
  const [selectedCategory, setSelectedCategory] = useState(initialCategory);
  const [currentPage, setCurrentPage] = useState(initialPage);

  const debouncedSearch = useDebounce(search, 300);
  const limit = 12;
  const totalPages = Math.ceil(total / limit);

  // Update URL when filters change
  const updateUrl = useCallback(
    (updates: Record<string, string | number>) => {
      const newParams = new URLSearchParams(searchParams);

      Object.entries(updates).forEach(([key, value]) => {
        if (value && value !== '' && value !== 1) {
          newParams.set(key, String(value));
        } else {
          newParams.delete(key);
        }
      });

      setSearchParams(newParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  // Fetch galleries
  useEffect(() => {
    async function loadGalleries() {
      try {
        setIsLoading(true);
        setError(null);

        // Build search query
        let searchQuery = debouncedSearch;
        if (selectedCategory) {
          searchQuery = searchQuery
            ? `${searchQuery} ${selectedCategory}`
            : selectedCategory;
        }

        const data = await api.getGalleries({
          page: currentPage,
          limit,
          search: searchQuery || undefined,
          sort,
        });

        setGalleries(data.galleries);
        setTotal(data.total);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load galleries');
      } finally {
        setIsLoading(false);
      }
    }

    loadGalleries();
  }, [debouncedSearch, sort, selectedCategory, currentPage]);

  // Update URL when filters change
  useEffect(() => {
    updateUrl({
      q: debouncedSearch,
      sort: sort === 'newest' ? '' : sort,
      category: selectedCategory,
      page: currentPage,
    });
  }, [debouncedSearch, sort, selectedCategory, currentPage, updateUrl]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, sort, selectedCategory]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCategoryClick = (category: string) => {
    if (selectedCategory === category) {
      setSelectedCategory('');
    } else {
      setSelectedCategory(category);
    }
  };

  const clearFilters = () => {
    setSearch('');
    setSort('newest');
    setSelectedCategory('');
    setCurrentPage(1);
  };

  const hasActiveFilters = search || sort !== 'newest' || selectedCategory;

  return (
    <div className="min-h-screen bg-gallery-950">
      {/* Header */}
      <div className="bg-gallery-900/50 border-b border-gallery-800">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <h1 className="font-display text-3xl md:text-4xl font-bold mb-2">
            Browse Galleries
          </h1>
          <p className="text-gallery-400">
            Discover curated digital art from talented artists worldwide
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Search and Filters */}
        <div className="mb-8 space-y-4">
          {/* Search Bar */}
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <svg
                className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gallery-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search galleries, artists, or tags..."
                className="w-full pl-12 pr-4 py-3 bg-gallery-900 border border-gallery-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-500 text-white placeholder-gallery-500"
                aria-label="Search galleries"
              />
              {search && (
                <button
                  onClick={() => setSearch('')}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gallery-500 hover:text-white"
                  aria-label="Clear search"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>

            {/* Sort Dropdown */}
            <div className="flex items-center gap-2">
              <label htmlFor="sort" className="text-gallery-400 text-sm whitespace-nowrap">
                Sort by:
              </label>
              <select
                id="sort"
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                className="px-4 py-3 bg-gallery-900 border border-gallery-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-500 text-white"
              >
                {SORT_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Category Tags */}
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map((category) => (
              <button
                key={category}
                onClick={() => handleCategoryClick(category)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  selectedCategory === category
                    ? 'bg-accent-600 text-white'
                    : 'bg-gallery-800 text-gallery-400 hover:text-white hover:bg-gallery-700'
                }`}
              >
                {category}
              </button>
            ))}
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="px-4 py-2 rounded-full text-sm font-medium text-red-400 hover:text-red-300 transition-colors"
              >
                Clear filters
              </button>
            )}
          </div>
        </div>

        {/* Results Count */}
        <div className="mb-6 flex items-center justify-between">
          <div className="text-gallery-400">
            {isLoading ? (
              <span className="animate-pulse bg-gallery-700 rounded h-5 w-32 inline-block" aria-hidden="true" />
            ) : (
              <>
                Showing {galleries.length} of {total.toLocaleString()} galleries
                {search && (
                  <span className="text-gallery-500">
                    {' '}
                    for "{search}"
                  </span>
                )}
              </>
            )}
          </div>
        </div>

        {/* Error State */}
        {error && (
          <div className="p-6 bg-red-900/20 border border-red-800 rounded-xl mb-8">
            <p className="text-red-400">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="mt-2 text-sm text-red-300 hover:text-red-200 underline"
            >
              Try again
            </button>
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="bg-gallery-900 rounded-xl overflow-hidden">
                <Skeleton className="aspect-[4/3] w-full rounded-none" />
                <div className="p-4">
                  <Skeleton className="h-6 w-3/4 mb-2" />
                  <Skeleton className="h-4 w-full mb-1" />
                  <Skeleton className="h-4 w-2/3" />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Gallery Grid */}
        {!isLoading && galleries.length > 0 && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {galleries.map((gallery) => (
              <GalleryCard key={gallery.id} gallery={gallery} />
            ))}
          </div>
        )}

        {/* Empty State */}
        {!isLoading && !error && galleries.length === 0 && (
          <div className="text-center py-16">
            <div className="w-20 h-20 mx-auto mb-4 bg-gallery-800 rounded-full flex items-center justify-center">
              <svg
                className="w-10 h-10 text-gallery-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
            </div>
            <h3 className="text-xl font-semibold mb-2">No galleries found</h3>
            <p className="text-gallery-400 mb-6">
              {search || selectedCategory
                ? "Try adjusting your search or filters"
                : "Be the first to create a gallery!"}
            </p>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="px-6 py-3 bg-accent-600 hover:bg-accent-700 rounded-lg font-medium transition-colors"
              >
                Clear filters
              </button>
            )}
          </div>
        )}

        {/* Pagination */}
        {!isLoading && totalPages > 1 && (
          <div className="mt-12">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </div>
    </div>
  );
}
